import React from 'react';
import type {
  ClassNameProp,
  ToolableComponentProps,
  VoidCallback
} from '../common-types';
import { HBox } from '../layout';
import { useAccessibleElementProps } from '../../lib';

/**
 * Props for a {@link PersonCard} component.
 * @author agd
 */
export interface PersonCardProps extends ClassNameProp, ToolableComponentProps {
  /**
   * Person picture element.
   */
  picture: React.ReactNode;
  /**
   * Content to be displayed in the card.
   * Generally, this is meant to be some formatted text containing the person's details.
   */
  content: React.ReactNode;
  /**
   * PersonCard void callback.
   * Supposed to include callback which is called on click event and
   * also keyboard event (when enter is pressed instead of click)
   */
  onClick?: VoidCallback;
}

/**
 * Component for displaying a person's details in a card-style layout.
 *
 * @param properties {@Link PersonCardProps} object.
 * @return the created {@link JSX.Element}.
 * @author agd
 * @constructor
 */
export function PersonCard({
  picture,
  content,
  tools,
  className = '',
  onClick
}: PersonCardProps): JSX.Element {
  const hboxProps = useAccessibleElementProps({
    className: `ehs-person-card ${className}`,
    onClick
  });

  return (
    <HBox {...hboxProps}>
      <div className="person-picture">{picture}</div>
      <HBox className="person-detail">
        <>{content}</>
      </HBox>
      {tools && <div className="person-card-tools">{tools}</div>}
    </HBox>
  );
}
